import React from "react";

import { Mail, Pencil } from "lucide-react";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  chronologicalColumn,
  dateColumn,
  statusColumn,
} from "../../dataTable/dataTableColumns";

import type { TFunction } from "i18next";

import type { TransformedGiftItem } from "./giftColumns.types";
import type {
  ColumnLayout,
  DataTableColumnDef,
} from "../../dataTable/dataTable.types";

interface GiftColumnsOptions {
  t: TFunction;
  canWrite: boolean;
  onResend: (uuid: string) => void;
  onEdit: (uuid: string) => void;
}

const TOKEN_COLUMN_LAYOUT: ColumnLayout = { width: "14%", minWidth: 127 };
const ACTIVATION_COLUMN_LAYOUT: ColumnLayout = { width: "16%", minWidth: 190 };
const DURATION_COLUMN_LAYOUT: ColumnLayout = { width: "12%", minWidth: 150 };

function formatMonths(months: number): string {
  return months.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export function giftColumns({
  t,
  canWrite,
  onResend,
  onEdit,
}: GiftColumnsOptions): DataTableColumnDef<TransformedGiftItem>[] {
  return [
    statusColumn<TransformedGiftItem>("status", t("status"), [
      { value: "active", label: t("active"), tone: "success" },
      { value: "waiting", label: t("waiting"), tone: "warning" },
      { value: "finished", label: t("finished"), tone: "neutral" },
      { value: "expired", label: t("expired"), tone: "error" },
    ]),
    {
      accessorKey: "email",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("email")} />
      ),
      meta: { searchable: true },
    },
    {
      accessorKey: "offer_name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("offer")} />
      ),
      meta: { searchable: true },
    },
    {
      accessorKey: "code",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("token")} />
      ),
      meta: { searchable: true, ...TOKEN_COLUMN_LAYOUT, align: "center" },
      cell: ({ row }) => (
        <span className="text-muted-foreground font-mono text-xs">
          {row.original.code}
        </span>
      ),
    },
    {
      ...chronologicalColumn<TransformedGiftItem>(
        "activationDate",
        (row) => row.activationDate,
      ),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("activationDate")} />
      ),
      meta: ACTIVATION_COLUMN_LAYOUT,
      cell: ({ row }) => {
        const {
          status,
          expire_date: expireDate,
          begin_date: beginDate,
          end_date: endDate,
        } = row.original;

        return status === "waiting" ? (
          <span
            data-tooltip-id={APP_TOOLTIP_ID}
            data-tooltip-content={t("freeAccessInactive", {
              expire_date: expireDate,
            })}
          >
            {expireDate}
          </span>
        ) : (
          <span
            data-tooltip-id={APP_TOOLTIP_ID}
            data-tooltip-content={t("freeAccessActive", {
              begin_date: beginDate,
              end_date: endDate,
            })}
          >
            {beginDate} — {endDate}
          </span>
        );
      },
    },
    {
      accessorKey: "month_duration",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("freeDuration")} />
      ),
      meta: { ...DURATION_COLUMN_LAYOUT, align: "center" },
      cell: ({ row }) =>
        `${formatMonths(row.original.month_duration)} ${t("months")}`,
    },
    dateColumn<TransformedGiftItem>("updated_at", t("update_date")),
    actionsColumn<TransformedGiftItem>(
      (gift) => {
        const isHidden = !canWrite || gift.status !== "waiting";

        return [
          {
            label: t("resendEmail"),
            icon: <Mail />,
            onClick: () => onResend(gift.uuid),
            hidden: isHidden,
          },
          {
            label: t("edit"),
            icon: <Pencil />,
            onClick: () => onEdit(gift.uuid),
            hidden: isHidden,
          },
        ];
      },
      (gift) => gift.email,
    ),
  ];
}
